import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Search } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import { useMutation } from "react-query";
import useUsers from "hooks/useUsers";
import { exportUsers } from "services/user.service";
import { User } from "types/user.type";
import { Pager } from "Common/Table";
import UsersTable from "./UsersTable";
import { segmentFor } from "./segments";

/**
 * One slice of the user base, on its own page.
 *
 * The dashboard's cards used to filter the list in place: the URL never
 * changed, so a segment could not be linked to, bookmarked, reloaded or
 * reached with the back button, and the heading above the table went on saying
 * "Registered users" whatever was actually in it. Giving each its own route
 * fixes all of that, and on a phone it also removes the wall of filter cards
 * you had to scroll past to reach the rows.
 *
 * The filters go to the API as exact `role` and `kycStatus` values — the same
 * ones behind the counts on the cards — so the number you clicked is the number
 * of rows you get.
 */
const UserSegment = () => {
  const { segment: key } = useParams<{ segment: string }>();
  const segment = segmentFor(key);
  const navigate = useNavigate();

  const { data, isLoading, onQueryChange, query } = useUsers({
    page: 1,
    limit: 20,
    search: "",
    role: segment?.role,
    kycStatus: segment?.kycStatus,
  });

  const { isLoading: exporting, mutate: doExport } = useMutation(
    async (input: { role: string; kycStatus: string }) =>
      await exportUsers(input.role, input.kycStatus),
    {
      onSuccess() {
        toast.success("Export ready");
      },
      onError(error: any) {
        toast.error(error?.response?.data?.message ?? "Export failed");
      },
    }
  );

  if (!segment) {
    // A hand-typed or stale URL. Say so rather than silently showing everyone,
    // which would look like the filter had simply failed to apply.
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <p className="text-sm text-gray-600 mb-3">
          There is no user group called “{key}”.
        </p>
        <Link
          to="/apps-users-list"
          className="text-sm font-medium text-indigo-600 hover:underline"
        >
          Back to all users
        </Link>
      </div>
    );
  }

  const total = data?.data?.total ?? 0;
  const page = Number(data?.data?.page ?? 1);
  const totalPages = Math.max(1, Math.ceil(total / (query.limit ?? 20)));

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} limit={1} />

      {/* Always visible, unlike BreadCrumb, which is desktop-only — on a phone
          this is the only thing naming the group and the only way back. */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Link
          to="/apps-users-list"
          aria-label="Back to all users"
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={16} />
          All users
        </Link>
        <h2 className="text-lg font-semibold text-gray-900">{segment.label}</h2>
        <span className="text-sm text-gray-500">
          {total} {total === 1 ? "record" : "records"}
        </span>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative grow sm:max-w-xs">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              onChange={({ target }) => onQueryChange("search", target.value)}
              placeholder={`Search within ${segment.label.toLowerCase()}`}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md"
            />
          </div>
          <button
            onClick={() =>
              doExport({
                role: segment.role ?? "",
                kycStatus: segment.kycStatus ?? "",
              })
            }
            disabled={exporting}
            type="button"
            className="inline-flex self-start items-center justify-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-60 transition-colors sm:ml-auto"
          >
            <Download className="inline-block size-4" />
            {exporting ? "Exporting…" : "Export"}
          </button>
        </div>

        <UsersTable
          users={data?.data?.data}
          total={total}
          isLoading={isLoading}
          emptyLabel={`No ${segment.label.toLowerCase()} found`}
          onView={(user: User) => navigate(`/pages-account/${user._id}`)}
        />

        <Pager
          page={page}
          totalPages={totalPages}
          total={total}
          onPage={(next) => onQueryChange("page", next)}
        />
      </div>
    </React.Fragment>
  );
};

export default UserSegment;
